import { Inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { filter, tap, withLatestFrom } from "rxjs/operators";

import { CurrencyService } from "@hermes/aphrodite/price";
import { LOCALE } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { selectUserType } from "@hermes/states/user";
import { AnalyticsService } from "@hermes/utils/analytics";
import { OriginCheckerService } from "@hermes/utils-generic/services/origin-checker";

import {
    clearFilters,
    initGridPage,
    loadMoreProducts,
    selectGridItem,
    sendPageViewEvent,
    updateGridFilter,
    updateGridFilterPrice,
    updateGridSort,
} from "../actions/grid.actions";
import {
    GridFilterChangedEvent,
    GridFilterChangedEventPayload,
} from "../events/grid-filter-changed.event";
import { GridFilterClearedEvent } from "../events/grid-filter-cleared.event";
import { GridFilterDisplayOnlyChangedEvent } from "../events/grid-filter-display-only-change.event";
import { GridFilterPriceChangedEvent } from "../events/grid-filter-price-changed.event";
import { GridFilterSortChangedEvent } from "../events/grid-filter-sort-changed.event";
import { GridLoadMoreEvent } from "../events/grid-load-more.event";
import { GridResultProductClickEvent } from "../events/grid-result-product-click.event";
import { SearchGridEvent } from "../events/grid-search.event";
import { getPageViewType } from "../helpers/gtm.helper";
import { GridType } from "../model/grid.model";
import {
    selectFacet,
    selectGridState,
    selectGridType,
    selectPageNumber,
} from "../selectors/grid.selectors";
import { FilterChangedService } from "../services/filter-changed-service/filter-changed.service";
import { GridAnalyticsService } from "../services/grid-analytics-service/grid-analytics.service";

import {
    getPageViewTypeOnGrid,
    isPageHasChanged,
} from "./grid-analytics.helper";

@Injectable()
export class GridAnalyticsEffects {
    public categoryWithSearchTerm$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(initGridPage),
                filter(
                    (action) =>
                        !!action.searchTerm &&
                        action.gridType === GridType.category &&
                        this.originCheckerService.checkReferrerOrigin(),
                ),
                tap((action) =>
                    this.analyticsService.sendData(
                        new SearchGridEvent({
                            eventlabel: action.searchTerm,
                            eventaction: "WithResultsGrid",
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    public filterChanged$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateGridFilter),
                tap((action) => {
                    if (action.filterGroup.presentation === "switch") {
                        /* Handle analytics event for 'available online' filter */
                        if (
                            /^display_state_.{2}$/.test(
                                action.filterGroup.attribute,
                            )
                        ) {
                            // Note: this works only because there is currently only "display_only" switch facet
                            // We cannot differentiate switch filters except by reading their "name"
                            this.analyticsService.sendData(
                                new GridFilterDisplayOnlyChangedEvent({
                                    eventType:
                                        action.filter.name?.toLowerCase() ===
                                        "on"
                                            ? "Display_only_yes"
                                            : "Display_only_no",
                                }),
                            );
                        }
                    } else {
                        if (action.filter.selected) {
                            this.analyticsService.sendData(
                                new GridFilterChangedEvent({
                                    gridFilter: action.filter,
                                    gridFilterGroupName:
                                        action.filterGroup.name,
                                    isHighlightFilter: action.isHighlightFilter,
                                } as GridFilterChangedEventPayload),
                            );
                        } else {
                            this.analyticsService.sendData(
                                new GridFilterClearedEvent(),
                            );
                        }
                    }
                    this.filterChangedService.filterChange();
                }),
            ),
        { dispatch: false },
    );

    public priceChanged$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateGridFilterPrice),
                tap((action) => {
                    this.analyticsService.sendData(
                        new GridFilterPriceChangedEvent(action.prices),
                    );
                    this.filterChangedService.filterChange();
                }),
            ),
        { dispatch: false },
    );

    public sortChanged$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(updateGridSort),
                tap((action) => {
                    this.analyticsService.sendData(
                        new GridFilterSortChangedEvent(action.sortBy),
                    );
                }),
            ),
        { dispatch: false },
    );

    public filtersCleared$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(clearFilters),
                tap((_action) => {
                    this.analyticsService.sendData(
                        new GridFilterClearedEvent(),
                    );
                }),
            ),
        { dispatch: false },
    );

    public loadMore$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(loadMoreProducts),
                withLatestFrom(this.store$.pipe(select(selectPageNumber))),
                tap(([_action, pageNumber]) => {
                    this.analyticsService.sendData(
                        new GridLoadMoreEvent({
                            label: `${pageNumber}`,
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );

    /**
     * Send Event for Grid Product Click event Analytics
     */
    public productClickEvent$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(selectGridItem),
                withLatestFrom(
                    this.store$.select(selectUserType),
                    this.store$.select(selectFacet),
                    this.store$.select(selectGridType),
                ),
                tap(
                    ([
                        { product, index, fromQuickbuy },
                        userType,
                        facet,
                        gridType,
                    ]) => {
                        const currencyMap =
                            this.currencyService.getCurrencyMap();
                        const currencyCode =
                            currencyMap && currencyMap.length > 0
                                ? currencyMap[1]
                                : "";
                        this.analyticsService.sendData(
                            new GridResultProductClickEvent({
                                product,
                                index,
                                fromQuickbuy,
                                pageType: getPageViewType(!!facet.missingFacet)[
                                    gridType
                                ],
                                clienttype: userType,
                                localeCode: this.locale.code,
                                currencyCode,
                            }),
                        );
                    },
                ),
            ),
        { dispatch: false },
    );

    // Send event on filter change - sort change - clear - initGrid page
    public sendEventCategoryOrFacetMetas$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(sendPageViewEvent),
                withLatestFrom(this.store$.select(selectGridState)),
                filter(([action, state]) =>
                    isPageHasChanged(
                        state.gridType,
                        action.previousPageHasChanged,
                    ),
                ),
                tap(([_action, state]) => {
                    const combinedFacets = state.facet?.combinedFacets;
                    const hasFacetName = !!state.facet?.facetName;
                    const missingFacet = state.facet?.missingFacet;

                    const pageViewType = getPageViewTypeOnGrid(
                        combinedFacets,
                        hasFacetName,
                        missingFacet,
                    );

                    this.gridAnalyticsService.sendGridPageViewEvent(
                        pageViewType,
                    );
                }),
            ),
        { dispatch: false },
    );

    constructor(
        @Inject(LOCALE) private locale: Locale,
        private actions$: Actions,
        private analyticsService: AnalyticsService,
        private currencyService: CurrencyService,
        private filterChangedService: FilterChangedService,
        private gridAnalyticsService: GridAnalyticsService,
        private originCheckerService: OriginCheckerService,
        private store$: Store,
    ) {}
}
